import { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { TimePicker } from '@/components/ui/TimePicker/TimePicker';
import { SingleDatePicker } from '@/components/ui';
import {
  MAX_SHIFT_CAPACITY,
  MAX_SHIFTS_PER_EVENT,
  eachDayInRange,
  eventDateToDayKey,
  formatShiftTime,
  groupShiftsByDay,
  localDateTime,
  type NewShiftInput,
  type ShiftDraft,
} from '@/utils/eventShifts';

interface ShiftsInputProps {
  shifts: ShiftDraft[];
  /** The event's date range as the form holds it. */
  eventStartDate: string;
  eventEndDate: string;
  loading?: boolean;
  /** Returns whether the shift was added, so the inputs only clear on success. */
  onAddShifts: (input: NewShiftInput) => boolean;
  onRemoveShift: (key: string) => void;
  onCapacityChange: (key: string, capacity: number | null) => void;
  disabled?: boolean;
}

function timeToMinutes(value: string | undefined): number | null {
  if (!value) return null;
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function minutesToTime(totalMinutes: number): string {
  const safe = Math.max(0, Math.min(23 * 60 + 59, totalMinutes));
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
}

/** Digits only; '' means no limit. Returns undefined to reject the keystroke. */
function parseSpots(raw: string): number | null | undefined {
  if (raw === '') return null;
  if (!/^\d+$/.test(raw)) return undefined;
  const n = Number(raw);
  if (n < 1) return undefined;
  return Math.min(n, MAX_SHIFT_CAPACITY);
}

const inputStyles = {
  h: '10',
  px: '3',
  bg: 'volyx.lightGray',
  borderRadius: 'lg',
  border: 'none',
  fontSize: 'sm',
  color: 'volyx.text',
  _focus: { outline: 'none', boxShadow: '0 0 0 3px rgba(31,41,50,0.1)' },
  _disabled: { opacity: 0.6, cursor: 'not-allowed' },
} as const;

export function ShiftsInput({
  shifts,
  eventStartDate,
  eventEndDate,
  loading = false,
  onAddShifts,
  onRemoveShift,
  onCapacityChange,
  disabled = false,
}: ShiftsInputProps) {
  const firstDay = eventDateToDayKey(eventStartDate);
  const lastDay = eventDateToDayKey(eventEndDate);
  const eventDays = useMemo(
    () => (firstDay && lastDay ? eachDayInRange(firstDay, lastDay) : []),
    [firstDay, lastDay],
  );
  const isMultiDay = eventDays.length > 1;

  const [day, setDay] = useState<string | null>(null);
  const [everyDay, setEveryDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('12:00');
  const [spots, setSpots] = useState('');

  // Keep the chosen day inside the event's dates when they change.
  useEffect(() => {
    if (eventDays.length === 0) {
      setDay(null);
      setEveryDay(false);
      return;
    }
    if (!isMultiDay) setEveryDay(false);
    setDay((current) => (current && eventDays.includes(current) ? current : eventDays[0]));
  }, [eventDays, isMultiDay]);

  const days = useMemo(() => groupShiftsByDay(shifts), [shifts]);

  const handleStartChange = (value: string) => {
    setStartTime(value);
    const start = timeToMinutes(value);
    const end = timeToMinutes(endTime);
    if (start != null && (end == null || end <= start)) setEndTime(minutesToTime(start + 60));
  };

  const handleEndChange = (value: string) => {
    const start = timeToMinutes(startTime);
    const end = timeToMinutes(value);
    if (start != null && end != null && end <= start) {
      setEndTime(minutesToTime(start + 5));
      return;
    }
    setEndTime(value);
  };

  const handleAdd = () => {
    const capacity = parseSpots(spots);
    const added = onAddShifts({
      days: everyDay ? eventDays : day ? [day] : [],
      startTime,
      endTime,
      capacity: capacity ?? null,
    });
    if (added) {
      setSpots('');
      setEveryDay(false);
    }
  };

  const canAdd = !disabled && eventDays.length > 0 && shifts.length < MAX_SHIFTS_PER_EVENT;

  return (
    <Box mb={{ base: '6', md: '4' }}>
      <Flex justify="space-between" align="center" mb="1">
        <Flex align="baseline" gap="1">
          <chakra.label
            htmlFor="event-shift-start-input"
            fontSize={{ base: 'sm', md: 'md' }}
            fontWeight="medium"
            color="volyx.text"
          >
            Shifts
          </chakra.label>
          <Text as="span" fontSize={{ base: '11px', md: 'xs' }} color="volyx.textSecondary">
            (optional)
          </Text>
        </Flex>
        <Text as="span" fontSize={{ base: 'xs', md: 'sm' }} color="volyx.textSecondary">
          ({shifts.length}/{MAX_SHIFTS_PER_EVENT})
        </Text>
      </Flex>
      <Text fontSize="xs" color="volyx.textSecondary" mb="3">
        Volunteers pick the shifts they can work when they apply. Leave spots empty for no limit.
      </Text>

      {loading && (
        <Text fontSize="sm" color="volyx.textSecondary" mb="3">
          Loading shifts…
        </Text>
      )}

      {days.length > 0 && (
        <Box mb="4" display="flex" flexDirection="column" gap="3">
          {days.map((group) => (
            <Box key={group.dayKey}>
              <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary" mb="1.5" textTransform="uppercase">
                {group.label}
              </Text>
              <Box display="flex" flexDirection="column" gap="1.5">
                {group.shifts.map((shift) => (
                  <Flex
                    key={shift.key}
                    align="center"
                    gap="2"
                    px="3"
                    py="2"
                    bg="volyx.lightGray"
                    borderRadius="lg"
                  >
                    <Box flex="1" minW="0">
                      <Text fontSize="sm" fontWeight="medium" color="volyx.text">
                        {formatShiftTime(shift)}
                      </Text>
                      {shift.taken > 0 && (
                        <Text fontSize="xs" color="volyx.textSecondary">
                          {shift.taken} signed up
                        </Text>
                      )}
                    </Box>
                    <chakra.input
                      aria-label={`Spots for ${group.label} ${formatShiftTime(shift)}`}
                      value={shift.capacity == null ? '' : String(shift.capacity)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const next = parseSpots(e.target.value);
                        if (next !== undefined) onCapacityChange(shift.key, next);
                      }}
                      placeholder="No limit"
                      inputMode="numeric"
                      maxLength={3}
                      disabled={disabled}
                      {...inputStyles}
                      w="84px"
                      bg="white"
                      textAlign="center"
                    />
                    <chakra.button
                      type="button"
                      aria-label={`Remove shift ${group.label} ${formatShiftTime(shift)}`}
                      p="1"
                      borderRadius="full"
                      border="none"
                      bg="transparent"
                      cursor="pointer"
                      display="flex"
                      _hover={{ bg: 'volyx.mediumGray' }}
                      onClick={() => onRemoveShift(shift.key)}
                      disabled={disabled}
                    >
                      <X size={16} color="var(--color-text-secondary)" />
                    </chakra.button>
                  </Flex>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {eventDays.length === 0 ? (
        <Text fontSize="sm" color="volyx.textSecondary">
          Choose the event dates first, then add shifts.
        </Text>
      ) : (
        <Box display="flex" flexDirection="column" gap="2">
          {isMultiDay && (
            <Flex align="center" gap="2">
              <Box flex="1" minW="0" opacity={everyDay ? 0.5 : 1}>
                <SingleDatePicker
                  popover
                  popoverPlacement="top"
                  // Handed over as local midnight: the picker reads a bare 'YYYY-MM-DD' as UTC.
                  value={day ? localDateTime(day, '00:00').toISOString() : null}
                  onChange={(value) => setDay(eventDateToDayKey(value))}
                  minDate={localDateTime(eventDays[0], '00:00')}
                  maxDate={localDateTime(eventDays[eventDays.length - 1], '00:00')}
                  placeholder="Day"
                  disabled={disabled || everyDay}
                />
              </Box>
              <chakra.button
                type="button"
                flexShrink={0}
                h="14"
                px="4"
                borderRadius="xl"
                fontSize="sm"
                fontWeight="medium"
                border="2px solid"
                borderColor={everyDay ? 'volyx.primary' : 'volyx.mediumGray'}
                bg={everyDay ? 'volyx.primaryLight' : 'white'}
                color="volyx.text"
                cursor="pointer"
                aria-pressed={everyDay}
                onClick={() => setEveryDay((v) => !v)}
                disabled={disabled}
              >
                Every day
              </chakra.button>
            </Flex>
          )}
          <Flex align="center" gap="2">
            <Box flex="1" minW="0">
              <TimePicker
                id="event-shift-start-input"
                value={startTime}
                onChange={handleStartChange}
                placeholder="Start"
                disabled={disabled}
                dropdownPlacement="top"
              />
            </Box>
            <Text as="span" color="volyx.textSecondary" fontWeight="medium" flexShrink={0}>
              -
            </Text>
            <Box flex="1" minW="0">
              <TimePicker
                id="event-shift-end-input"
                value={endTime}
                onChange={handleEndChange}
                placeholder="End"
                disabled={disabled}
                min={startTime || undefined}
                dropdownPlacement="top"
              />
            </Box>
          </Flex>
          <Flex align="center" gap="2">
            <chakra.input
              id="event-shift-spots-input"
              aria-label="Spots"
              value={spots}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const next = parseSpots(e.target.value);
                if (next !== undefined) setSpots(next == null ? '' : String(next));
              }}
              placeholder="Spots (no limit)"
              inputMode="numeric"
              maxLength={3}
              disabled={disabled}
              {...inputStyles}
              h="14"
              flex="1"
              minW="0"
              borderRadius="xl"
            />
            <chakra.button
              type="button"
              h="14"
              px="6"
              flexShrink={0}
              borderRadius="xl"
              fontWeight="semibold"
              border="none"
              bg="volyx.action"
              color="white"
              cursor={canAdd ? 'pointer' : 'not-allowed'}
              opacity={canAdd ? 1 : 0.6}
              boxShadow="0 4px 12px rgba(0,0,0,0.08)"
              _hover={canAdd ? { bg: 'rgba(0,122,255,0.9)' } : undefined}
              onClick={handleAdd}
              disabled={!canAdd}
            >
              {everyDay ? `Add ${eventDays.length}` : 'Add'}
            </chakra.button>
          </Flex>
        </Box>
      )}
    </Box>
  );
}
